import { AxiosError } from 'axios';
import { clsx } from 'clsx';
import {
  FunctionComponent,
  KeyboardEvent,
  RefObject,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Subscription } from 'rxjs';
import { DEFAULT_LIMIT } from '../list-conductor/constants';
import { ListConductorFilters } from '../list-conductor/filters';
import { ListNextPage, SortList } from '../list-conductor/list-actions';
import { ListConductor } from '../list-conductor/list-conductor';
import { ListChanges, ListMetaData, ListMetaNetworkParams } from '../list-conductor/list-meta-data';
import {
  OffsetPaginationData,
  OffsetPaginationStrategy
} from '../list-conductor/offset-pagination-strategy';
import { SortDirection } from '../list-conductor/sort';
import { PaginationData } from '../pagination-controls/interfaces';
import { PaginatedResponse, QueryResult, useQuery } from '../request/hooks/useQuery';
import { BackendSubscriptionQuery } from '../request/interfaces/backend-subscription';
import { EntityUIState } from '../request/interfaces/entity-ui-state';
import { getErrorMessageFromAxiosError } from '../request/utils';
import { EmptyWidget } from '../shared/components/EmptyWidget/EmptyWidget';
import { RetryApiAlert } from '../shared/components/RetryApiAlert/RetryApiAlert';
import { useMemoizedDataIds } from '../shared/utils/dataId';
import { GenericPageShimmer } from '../shimmer/GenericPageShimmer/GenericPageShimmer';
import { TableShimmer } from '../table/TableShimmer';
import { useAnalyticsTracker } from '../tracking/hooks/useAnalyticsTracker';
import { ENTITY_LIST_DATA_IDS } from './data-ids';
import { EntityListTrackers } from './entity-list-trackers';
import { EntityListToolbar } from './EntityListToolbar';
import {
  EntityListMode,
  EntityListNavigationType,
  EntityListNonIdleProps,
  ListComponentBaseProps,
  StandaloneModeActionsComponent
} from './interface';
import styles from './styles.module.scss';
import { useKeyPressNavigation } from './useKeyPressNavigation';

export type EntityListProps<P, D extends object, S extends object, T, F, N extends object, O> = {
  innerRef?: RefObject<unknown>;
  dataId?: string;
  classes?: {
    className?: string;
    listClassName?: string;
    listContainerClassName?: string;
    toolbar?: string;
  };
  mode: EntityListMode;
  navigationType?: EntityListNavigationType;
  showCount?: boolean;
  compact?: boolean;
  searchKey?: string;
  showFilters?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  hideRefresh?: boolean;
  showRefreshInNewList?: boolean;
  shimmerColumnsCount?: number;
  genericPageShimmer?: boolean;
  disableFilters?: boolean;
  entityTitle?: string;
  newListConnectedTitle: string;
  newListConnectedSubTitle?: string;
  genericErrorTitle: string;
  genericErrorSubTitle: string;
  ListComponent: FunctionComponent<T & ListComponentBaseProps<D[]>>;
  FiltersComponent?: FunctionComponent<F & { listConductor: ListConductor }>;
  filtersComponentProps?: F;
  SortComponent?: FunctionComponent<O & { listConductor: ListConductor; className?: string }>;
  sortComponentProps?: O;
  listComponentProps?: T;
  listIcon: string;
  emptyTitle: string;
  emptySubTitle: string;
  NewListComponent?: FunctionComponent<N>;
  customNewListComponentProps?: N;
  dataSource$: (params?: P | ListMetaNetworkParams) => Promise<QueryResult<D[]>>;
  subscriptionQueries?: BackendSubscriptionQuery<D[]>[];
  StandaloneModeActions?: StandaloneModeActionsComponent<S>;
  standaloneModeActionsProps?: S;
  inMemoryList?: boolean;
  hidePagination?: boolean;
  defaultListMeta?: ListMetaData<OffsetPaginationData>;
  onRefresh?: VoidFunction;
  onStateChange?: (p: { data: unknown; filters?: ListConductorFilters }) => void;
  onListMetaChange?: (changes: ListChanges<OffsetPaginationData>) => void;
  entity: string;
  pageLimit?: number;
  showOperatorInSearch?: boolean;
  withInfiniteScroll?: boolean;
  ShimmerComponent?: FunctionComponent;
  bulkSelection?: boolean;
  selectedItems?: Set<number | string>;
  onItemSelectionChange?: (row: D, selected: boolean) => void;
  onAllItemsSelectionChange?: (selected: boolean) => void;
  onAllInPageSelectionChange?: (selected: boolean) => void;
  BulkActionsComponent?: FunctionComponent<object>;
  bulkActionsComponentProps?: object;
  backgroundDataSource$?: (params?: P | ListMetaNetworkParams) => Promise<QueryResult<D[]>>;
  pollDataSource$?: (currentData?: D[]) => Promise<D[]>;
  dataMergeFn?: (foregroundData: D[], backgroundData: D[]) => D[];
  compactControls?: boolean;
} & EntityListNonIdleProps;

export function EntityList<P, D extends object, S extends object, T, F, N extends object, O>({
  innerRef,
  dataId,
  classes,
  mode,
  navigationType = EntityListNavigationType.PAGE_NUMBER,
  showCount,
  compact,
  searchKey,
  showSearch,
  searchPlaceholder,
  NewListComponent,
  ListComponent,
  dataSource$,
  ShimmerComponent,
  shimmerColumnsCount,
  genericPageShimmer,
  disableFilters,
  showFilters,
  hideRefresh,
  showRefreshInNewList,
  StandaloneModeActions,
  standaloneModeActionsProps,
  listComponentProps,
  entityTitle,
  newListConnectedTitle,
  newListConnectedSubTitle,
  genericErrorTitle,
  genericErrorSubTitle,
  listIcon,
  emptyTitle,
  emptySubTitle,
  errorComponentProps,
  newListComponentProps,
  customNewListComponentProps,
  inMemoryList,
  hidePagination,
  defaultListMeta,
  onRefresh,
  onStateChange,
  onListMetaChange,
  entity,
  pageLimit = DEFAULT_LIMIT,
  FiltersComponent,
  filtersComponentProps,
  SortComponent,
  sortComponentProps,
  showOperatorInSearch = false,
  withInfiniteScroll = false,
  bulkSelection = false,
  selectedItems = new Set([]),
  onItemSelectionChange = () => {},
  onAllItemsSelectionChange = () => {},
  onAllInPageSelectionChange = () => {},
  bulkActionsComponentProps,
  BulkActionsComponent,
  pollDataSource$,
  backgroundDataSource$,
  compactControls,
  subscriptionQueries,
  dataMergeFn
}: EntityListProps<P, D, S, T, F, N, O>) {
  const { eventTrack } = useAnalyticsTracker();

  const { fetch, listConductor, uiState, error, data, shouldDisableFilters, writeData, reset } =
    useQuery({
      dataSource$,
      pollDataSource$,
      subscriptionQueries,
      paginated: true,
      listConductorOptions: {
        handleQueryParams: false,
        paginationStrategy: hidePagination ? undefined : new OffsetPaginationStrategy(pageLimit),
        defaultListMeta,
        inMemoryList
      },
      fetchSuccess: foregroundDataRes => {
        if (backgroundDataSource$) {
          backgroundDataSource$(listConductor?.getListMetaNetworkParams()).then(res => {
            const backgroundData = (res as PaginatedResponse<D[]>).items;

            if (dataMergeFn) {
              const newData = dataMergeFn(foregroundDataRes as D[], backgroundData);
              writeData(() => newData);
            }
          });
        }
      }
    });

  useImperativeHandle(
    innerRef,
    () => ({
      reset
    }),
    [reset]
  );

  const listRef = useRef(null);

  const { focusIndex, handleKeyPress, setFocusIndex, clearFocusIndex, resetScroll } =
    useKeyPressNavigation(listRef);

  const totalCount = (listConductor?.getListMeta().pagination as PaginationData)?.totalCount;

  const [sentryRef] = useInfiniteScroll({
    loading: uiState === EntityUIState.LOADING || uiState === EntityUIState.LOADING_MORE,
    hasNextPage: (data?.length || 0) < totalCount,
    onLoadMore: () => {
      if (withInfiniteScroll) {
        listConductor?.dispatch(new ListNextPage());
      }
    },
    disabled: false,
    rootMargin: '0px 0px 200px 0px'
  });

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        filters: listConductor?.getListMeta().filters,
        data
      });
    }
  }, [data, listConductor?.getListMeta().filters]);

  useEffect(() => {
    let sub = Subscription.EMPTY;

    if (listConductor && typeof onListMetaChange === 'function') {
      sub = listConductor.changes$.subscribe(change => {
        onListMetaChange(change);
      });
    }

    return () => {
      sub.unsubscribe();
    };
  }, [onListMetaChange]);

  const refreshList = () => {
    if (typeof onRefresh === 'function') {
      onRefresh();
    }

    resetScroll();

    fetch();
  };

  const listComponentRenderedProps = {
    data: data as D[],
    listMode: mode,
    listRef,
    writeData,
    tableProps: {
      withoutShadow: mode === 'connected',
      compactHeader: mode === 'connected'
    },
    refresh: refreshList,
    getNext: () => {
      listConductor?.dispatch(new ListNextPage());
    },
    handleKeyPress,
    focusIndex,
    ...listComponentProps
  } as T & ListComponentBaseProps<D[]>;

  if (!hidePagination && listConductor) {
    listComponentRenderedProps.tableProps = {
      ...listComponentRenderedProps.tableProps,
      sortState: listConductor.getListMeta().sort,
      onSort: (sortKey: string, order: SortDirection) => {
        listConductor.dispatch(new SortList({ key: sortKey, order }));
      }
    };
  }

  if (bulkSelection) {
    listComponentRenderedProps.tableProps = {
      ...listComponentRenderedProps.tableProps,
      bulkSelection,
      selectedItems,
      onItemSelectionChange,
      onAllInPageSelectionChange,
      totalCount,
      onAllItemsSelectionChange,
      BulkActionsComponent,
      bulkActionsComponentProps
    };
  }

  const showEntityListToolbar =
    uiState === EntityUIState.IDLE ||
    uiState === EntityUIState.REFRESHING ||
    uiState === EntityUIState.EMPTY ||
    uiState === EntityUIState.LOADING ||
    uiState === EntityUIState.LOADING_MORE;

  const entityListDataIds = useMemoizedDataIds(ENTITY_LIST_DATA_IDS, dataId);

  return (
    <div
      data-id={entityListDataIds.bodyContainer}
      className={clsx('body-container w-100 h-100', classes?.className)}
    >
      <div
        data-id={entityListDataIds.container}
        className={clsx(
          styles.listContainer,
          mode === 'connected' && styles.connected,
          'h-100',
          classes?.listContainerClassName
        )}
      >
        <div
          data-id={entityListDataIds.content}
          className={clsx(
            styles.list,
            mode === 'connected' && styles.connectedList,
            classes?.listClassName
          )}
        >
          {showEntityListToolbar ? (
            <EntityListToolbar
              entity={entity}
              dataId=''
              title={entityTitle}
              navigationType={navigationType}
              className={classes?.toolbar}
              onRefresh={!hideRefresh ? refreshList : undefined}
              listConductor={listConductor as ListConductor}
              mode={mode}
              searchKey={searchKey}
              defaultListMeta={defaultListMeta}
              uiState={uiState}
              showFilters={showFilters}
              disableFilters={shouldDisableFilters || disableFilters}
              FiltersComponent={FiltersComponent}
              filtersComponentProps={filtersComponentProps}
              SortComponent={SortComponent}
              sortComponentProps={sortComponentProps}
              showSearch={showSearch}
              StandaloneModeActions={StandaloneModeActions}
              standaloneModeActionsProps={standaloneModeActionsProps}
              searchPlaceholder={searchPlaceholder}
              showCount={showCount}
              compact={compact}
              onSearchAreaKeydown={e => handleKeyPress(e as KeyboardEvent<HTMLDivElement>)}
              onSearchAreaBlur={clearFocusIndex}
              onSearchAreaFocus={() => setFocusIndex(0)}
              showOperatorInSearch={showOperatorInSearch}
              compactControls={compactControls}
            />
          ) : null}

          {uiState === EntityUIState.NEW && mode !== 'connected' && NewListComponent ? (
            <NewListComponent {...(customNewListComponentProps as N)} />
          ) : null}

          {uiState === EntityUIState.NEW && (mode === 'connected' || !NewListComponent) ? (
            <EmptyWidget
              className='h-100 w-100 my-9'
              icon={listIcon}
              title={newListConnectedTitle}
              subTitle={newListConnectedSubTitle}
              secondaryCTAProps={
                showRefreshInNewList
                  ? {
                      children: 'Refresh',
                      onClick: () => {
                        fetch();
                      }
                    }
                  : undefined
              }
              {...newListComponentProps}
            />
          ) : null}

          {uiState === EntityUIState.LOADING || uiState === EntityUIState.LOADING_MORE ? (
            <>
              {genericPageShimmer ? <GenericPageShimmer dataId='' className='h-100 my-9' /> : null}

              {!genericPageShimmer && shimmerColumnsCount ? (
                <TableShimmer
                  rowsCount={5}
                  columnsCount={shimmerColumnsCount}
                  tableProps={{
                    withoutShadow: listComponentRenderedProps.tableProps.withoutShadow,
                    compactHeader: listComponentRenderedProps.tableProps.compactHeader,
                    classes: listComponentRenderedProps.tableProps?.classes
                  }}
                />
              ) : null}

              {ShimmerComponent ? <ShimmerComponent /> : null}
            </>
          ) : null}

          {uiState === EntityUIState.ERRORED ? (
            <RetryApiAlert
              title={genericErrorTitle}
              subTitle={getErrorMessageFromAxiosError(error as AxiosError) || genericErrorSubTitle}
              {...errorComponentProps}
              secondaryCTAProps={{
                onClick: () => {
                  eventTrack({
                    action: EntityListTrackers.TRACKER_ENTITY_LIST_RETRY_API_CLICK,
                    properties: {
                      entity,
                      error: getErrorMessageFromAxiosError(error as AxiosError)
                    }
                  });

                  fetch();
                }
              }}
              className='h-100 w-100 my-9'
            />
          ) : null}

          {uiState === EntityUIState.IDLE ||
          (data?.length && uiState === EntityUIState.REFRESHING) ? (
            <>
              <ListComponent {...listComponentRenderedProps} />
              <div ref={sentryRef} />
            </>
          ) : null}

          {uiState === EntityUIState.EMPTY ? (
            <EmptyWidget
              className='h-100 w-100 my-9'
              title={emptyTitle}
              subTitle={emptySubTitle}
              icon={listIcon}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
